import {DbCollectionFactory} from "factories/DbCollectionFactory";
import {createContext, useCallback, useContext, useEffect, useMemo, useRef, useState} from "react";
import type {ReactNode} from "react";
import {DB_COLLECTION_NAMES} from "constants/AppConstants.js";
import {AppUser} from "services/UserService.js";
import {useAuth} from "contexts/AuthContext";
import {FirebaseMessaging} from "@capacitor-firebase/messaging";
import {Capacitor, registerPlugin} from "@capacitor/core";
import {isNil} from "lodash";
import {isAndroid} from "FirebaseUtils";
import {Directory, Filesystem} from "@capacitor/filesystem";
import {App} from "@capacitor/app";
import {LocalDbService} from "services/LocalDBService";
import LogService from "services/LogService";
import {datadogLogs} from "@datadog/browser-logs";
import RemoteConfigService from "services/RemoteConfigService";
import SubscriptionService from "services/SubscriptionService";
import {getProfileLmd, isLocalProfileNewer, mergeUserWithProfileSource} from "services/FirebaseDbService";

const AppUserContext = createContext<any>(undefined);
const userCollection: any = DbCollectionFactory.create(DB_COLLECTION_NAMES.users);
const UserContextPlugin = registerPlugin<any>("UserContextPlugin");

interface Props {
  children: ReactNode;
}

function AppUserProvider({children}: Props) {
  const {user, claims, isAuthenticating} = useAuth();
  const [appUser, setAppUser] = useState<any>(null);
  const [forceRefresh, setForceRefresh] = useState(0);
  const appUserRef = useRef<any>(appUser);
  const hasAlreadyBeenExecuted = useRef(false);
  const syncFinished = useRef(false);
  const lastSubscriptionBackfill = useRef<string | null>(null);
  const [syncFinishedState, setSyncFinished] = useState(false);

  useEffect(() => {
    RemoteConfigService.configure();
  }, []);

  useEffect(() => {
    (async () => {
      if (user && !isAuthenticating) {
        const appUserFetched = await fetchData();
        setAppUser(appUserFetched);
      }
    })();
    return () => {
      setAppUser(null);
      appUserRef.current = null;
    };
  }, [user, isAuthenticating, forceRefresh]);

  // Set Datadog user context on web when appUser is available
  useEffect(() => {
    if (isAndroid) {
      try {
        if (appUser?.uid) {
          datadogLogs.setUser({id: appUser.uid});
        } else {
          datadogLogs.clearUser();
        }
      } catch (e) {
        console.error("Error setting Datadog user context", e);
      }
    }
  }, [appUser]);

  useEffect(() => {
    if (appUser && !hasAlreadyBeenExecuted.current && syncFinished.current) {
      (async () => {
        const loginTimestamp = new Date().getTime() / 1000;
        if (isAndroid) {
          appUserRef.current.userInDb.lastLoginAndroid = loginTimestamp;
          await pushUserFieldsAsync({lastLoginAndroid: loginTimestamp});
          registerNotifications();
        } else {
          appUserRef.current.userInDb.lastLoginWeb = loginTimestamp;
          await pushUserFieldsAsync({lastLoginWeb: loginTimestamp});
        }
        setAppUser(appUserRef.current);
        hasAlreadyBeenExecuted.current = true;
      })();
    }
  }, [appUser, syncFinishedState]);

  useEffect(() => {
    let logListenerHandle = null;
    let resumeListenerHandle = null;
    // Cleanup can run before the awaited addListener below resolves.
    let cleanedUp = false;

    if (isAndroid) {
      if (appUser) {
        UserContextPlugin.setUserData(appUserRef.current);

        (async () => {
          logListenerHandle = LogService.initializeAppStateListener(appUser.uid);

          if (syncFinished.current) {
            await updateSubscriptionExpiryDateFromAdapty();
          }

          // Add listener to refresh subscription when app comes back to foreground
          resumeListenerHandle = await App.addListener("resume", async () => {
            console.log("App resumed - refreshing subscription data from Adapty");
            // A subscription that lapsed while backgrounded reports the same
            // expiration, so nothing "changes" but the gates still must re-evaluate.
            await updateSubscriptionExpiryDateFromAdapty({forcePublish: true});
          });
          if (cleanedUp) {
            resumeListenerHandle.remove();
            resumeListenerHandle = null;
          }
        })();
      }

      return () => {
        cleanedUp = true;
        UserContextPlugin.setUserData(null);
        if (logListenerHandle) {
          logListenerHandle.remove();
        }
        if (resumeListenerHandle) {
          resumeListenerHandle.remove();
        }
      };
    }
  }, [appUser, syncFinished.current]);

  const updateSubscriptionExpiryDateFromAdapty = async ({forcePublish = false} = {}) => {
    if (!appUserRef.current?.userInDb) {
      return;
    }

    // Fetch subscription data from Android (read-only on web)
    const subscriptionData = await UserContextPlugin.getAllSubscriptionData().catch((e) => {
      console.error("Error getting subscription data from Adapty", e);
      return {
        premiumExpiration: null,
        premiumIsTrial: false,
        premiumState: "undefined",
        ultraExpiration: null,
        ultraIsTrial: false,
        ultraState: "undefined"
      };
    });

    // Check again after async call - appUserRef.current might have changed
    if (!appUserRef.current) {
      console.warn("appUserRef.current became null during subscription update");
      return;
    }

    // Update LOCAL state only for UI reactivity
    // Android handles all subscription writes to Firebase
    const premiumExpirationUnix = subscriptionData.premiumExpiration;
    const ultraExpirationUnix = subscriptionData.ultraExpiration;

    // Snapshot before mutating: the caller effect re-runs on every appUser change,
    // so publishing unconditionally would loop.
    const previousPremiumExpiration = appUserRef.current.userInDb.subscription?.premium?.expiration ?? null;
    const previousUltraExpiration = appUserRef.current.userInDb.subscription?.ultra?.expiration ?? null;

    // Store in local state for UI (not persisted to Firebase from web)
    appUserRef.current.premiumSubscription = {
      expiration: subscriptionData.premiumExpiration,
      isTrial: subscriptionData.premiumIsTrial,
      state: subscriptionData.premiumState
    };

    appUserRef.current.ultraSubscription = {
      expiration: subscriptionData.ultraExpiration,
      isTrial: subscriptionData.ultraIsTrial,
      state: subscriptionData.ultraState
    };

    // Update userInDb.subscription so isUserUltra() and isUserPremium() work correctly
    if (!appUserRef.current.userInDb.subscription) {
      appUserRef.current.userInDb.subscription = {};
    }
    if (premiumExpirationUnix) {
      appUserRef.current.userInDb.subscription.premium = {
        expiration: premiumExpirationUnix,
        isTrial: subscriptionData.premiumIsTrial
      };
    }
    if (ultraExpirationUnix) {
      appUserRef.current.userInDb.subscription.ultra = {
        expiration: ultraExpirationUnix,
        isTrial: subscriptionData.ultraIsTrial
      };
    }

    // Guarded so a null read cannot wipe a stored expiry.
    if (premiumExpirationUnix || ultraExpirationUnix) {
      appUserRef.current.subscriptionExpiryDateInUnixTimestamp = premiumExpirationUnix || ultraExpirationUnix;
    }

    const hasChanged =
      (!!premiumExpirationUnix && premiumExpirationUnix !== previousPremiumExpiration) ||
      (!!ultraExpirationUnix && ultraExpirationUnix !== previousUltraExpiration);

    if (!hasChanged) {
      if (forcePublish) {
        publishAppUser();
      }
      return;
    }

    // Nothing else persists the entitlement, so it would be lost on the next cold
    // start. Never awaited. Keyed in a ref because `hasChanged` compares against
    // `userInDb`, which every fetchData() replaces — that would re-fire all session
    // whenever the write does not land.
    const backfillKey = `${appUserRef.current.uid}:${premiumExpirationUnix ?? ""}:${ultraExpirationUnix ?? ""}`;
    if (lastSubscriptionBackfill.current !== backfillKey) {
      lastSubscriptionBackfill.current = backfillKey;
      SubscriptionService.refreshSubscriptionFromAdapty()
        .then((result) => {
          // Resolves even on failure, and reports success while writing nothing when
          // the profile has no access levels.
          const persisted = result?.subscription?.premium?.expiration || result?.subscription?.ultra?.expiration;
          if (!result?.success || !persisted) {
            console.warn("Subscription backfill did not persist:", result?.error ?? "no expiration returned");
          }
        })
        .catch((error) => {
          console.error("Error persisting subscription to Firebase", error);
        });
    }

    publishAppUser();
  };

  // The refresh mutates `appUserRef.current` in place, which React cannot see:
  // consumers memoise their Pro check on the appUser identity, so they need a new
  // instance to recompute.
  const publishAppUser = () => {
    if (!appUserRef.current?.userInDb) {
      return;
    }
    const updatedAppUser: any = new AppUser(
      {uid: appUserRef.current.uid, img: appUserRef.current.img},
      appUserRef.current.userInDb
    );
    updatedAppUser.imgSrc = appUserRef.current.imgSrc;
    updatedAppUser.premiumSubscription = appUserRef.current.premiumSubscription;
    updatedAppUser.ultraSubscription = appUserRef.current.ultraSubscription;
    appUserRef.current = updatedAppUser;
    setAppUser(updatedAppUser);
  };

  const syncPrivacyOptions = async (localUser, wait) => {
    // Read privacy options from remote database, similar to AndroidSettingsPage
    if (localUser) {
      try {
        const remoteUser = await userCollection.getUserByIdAsync(localUser.uid, true, true);

        if (remoteUser?.privacyOptions) {
          // Merge privacy options from remote into local user properties
          localUser.userInDb.privacyOptions = remoteUser.privacyOptions;
          // Also update the privacyOptions property on the AppUser instance
          localUser.privacyOptions = remoteUser.privacyOptions;

          appUserRef.current = localUser;
          // Update React state so components re-render with updated privacy options
          if (!wait) {
            setAppUser(localUser);
          }
        }
      } catch (error) {
        console.error("Error reading privacy options from remote database", error);
      }
    }
  };

  const fetchData = async (wait = false) => {
    if (isAndroid) {
      // Sync data with local user
      const localUser = await fetchDataLocal();

      // Merge localUser into appUserRef.current, preserving existing privacyOptions
      if (appUserRef.current?.userInDb?.privacyOptions) {
        localUser.userInDb.privacyOptions = {
          ...localUser.userInDb.privacyOptions,
          ...appUserRef.current.userInDb.privacyOptions
        };
      }
      appUserRef.current = localUser;

      // Sync data with remote user
      if (wait) {
        await syncUser(localUser, wait);
      } else {
        syncUser(localUser);
      }

      if (wait) {
        await syncPrivacyOptions(localUser, wait);
        // Update state after syncing privacy options when wait=true
        setAppUser(appUserRef.current);
      } else {
        syncPrivacyOptions(localUser, wait);
      }
    } else {
      const userRemote = await fetchDataRemote();
      const currentUser = appUserRef.current;
      const profileLmdChanged =
        currentUser?.userInDb &&
        userRemote?.userInDb &&
        getProfileLmd(currentUser.userInDb) !== getProfileLmd(userRemote.userInDb);
      const shouldRefresh =
        (wait && userRemote) ||
        (!currentUser && userRemote) ||
        (userRemote && currentUser && currentUser.userInDb.lmd !== userRemote.userInDb.lmd) ||
        profileLmdChanged;

      if (shouldRefresh && userRemote) {
        if (currentUser?.userInDb && isLocalProfileNewer(currentUser.userInDb, userRemote.userInDb)) {
          const mergedInDb = mergeUserWithProfileSource(userRemote.userInDb, currentUser.userInDb, user.uid);
          const mergedAppUser: any = new AppUser(user, mergedInDb);
          mergedAppUser.imgSrc = currentUser.imgSrc || userRemote.imgSrc;
          appUserRef.current = mergedAppUser;
          await userCollection.syncProfileToFirebaseAsync(user.uid, mergedInDb, true);
        } else {
          appUserRef.current = userRemote;
        }
        setAppUser(appUserRef.current);
        if (wait) {
          await syncPrivacyOptions(appUserRef.current, wait);
          setAppUser(appUserRef.current);
        } else {
          syncPrivacyOptions(appUserRef.current, wait);
        }
      }
    }
    return appUserRef.current;
  };

  const fetchDataLocal = async () => {
    /**
     * This function will fetch the user from the local database (SQLlite local DB).
     */
    const userInDb = await (await LocalDbService.getLocalDbService()).getUser(user.uid);
    let userToReturn = null;

    if (!isNil(userInDb)) {
      userToReturn = new AppUser(user, userInDb);
      const fileUri = await Filesystem.getUri({
        path: `img${user.uid}.jpg`,
        directory: Directory.Data
      }).catch(() => null);

      if (fileUri) {
        userToReturn.imgSrc = Capacitor.convertFileSrc(fileUri.uri);
      }
      localStorage.setItem("WS_User", user.uid);
    } else if (user) {
      // Initialize new user in local DB with complete data
      const currentDate = new Date();
      const newUser = {
        ID: user.uid,
        email: user.email || user.providerData.find((provider) => provider.email)?.email,
        name: user.displayName,
        firstLogin: currentDate.getTime() / 1000,
        firstLoginFull: currentDate.toISOString(),
        subscriptionExpDate: null,
        subscriptionExpDateFull: null,
        lmd: currentDate.getTime() / 1000,
        lmdFull: currentDate.toISOString(),
        createdFromAndroid: isAndroid,
        lastLoginAndroid: currentDate.getTime() / 1000
      };
      const localDbService = await LocalDbService.getLocalDbService();
      await localDbService.clear();
      await localDbService.setUser(newUser);
      userToReturn = new AppUser(user, newUser);
      localStorage.setItem("WS_User", user.uid);
    }

    return userToReturn;
  };

  const syncUser = async (_localUser, wait = false) => {
    /**
     * Sync local Capacitor user with Firebase.
     * - Profile (name, etc.): compared via profileLmd — newer side wins.
     * - Subscription/entitlement: always taken from Firebase (native/Adapty owns these).
     */
    let localUser;
    if (_localUser) {
      localUser = _localUser;
    } else {
      localUser = await fetchDataLocal();
    }

    const userRemote = await fetchDataRemote();

    if (userRemote) {
      let normalizedEntitlement = null;
      if (userRemote.userInDb.entitlement) {
        if (Array.isArray(userRemote.userInDb.entitlement)) {
          normalizedEntitlement = userRemote.userInDb.entitlement;
        } else if (typeof userRemote.userInDb.entitlement === "object") {
          normalizedEntitlement = userRemote.userInDb.entitlement;
        } else {
          try {
            normalizedEntitlement = JSON.parse(userRemote.userInDb.entitlement);
          } catch (e) {
            normalizedEntitlement = null;
          }
        }
      }

      const profileSource = isLocalProfileNewer(localUser?.userInDb, userRemote.userInDb)
        ? localUser?.userInDb
        : userRemote.userInDb;

      const userToSave = mergeUserWithProfileSource(
        userRemote.userInDb,
        profileSource,
        user.uid,
        normalizedEntitlement
      );

      if (isAndroid) {
        await (await LocalDbService.getLocalDbService()).setUser(userToSave);

        const mergedAppUser: any = new AppUser(user, userToSave);
        mergedAppUser.imgSrc = localUser?.imgSrc || userRemote.imgSrc;
        appUserRef.current = mergedAppUser;
        if (!wait) {
          setAppUser(mergedAppUser);
        }

        await saveImageToNativeFS(mergedAppUser).catch((e) => {
          console.error(e);
        });
      }

      if (isLocalProfileNewer(localUser?.userInDb, userRemote.userInDb) && localUser?.userInDb) {
        await userCollection.syncProfileToFirebaseAsync(user.uid, userToSave, true);
      }
    } else if (localUser?.userInDb) {
      await userCollection.syncProfileToFirebaseAsync(localUser.uid, localUser.userInDb, true);
      appUserRef.current = localUser;
      if (!wait) {
        setAppUser(localUser);
      }
    }

    if (isAndroid) {
      const targetUser = appUserRef.current || localUser;
      const updatedImage = await ensureLocalImageFromRemote(targetUser, userRemote);
      if (updatedImage && !wait) {
        setAppUser(targetUser);
      }
    }

    const appInfo = await App.getInfo();
    const appVersion = appInfo.version;
    const build = appInfo.build;

    if (
      appUserRef.current?.userInDb &&
      (appUserRef.current.userInDb.gAppVersion !== appVersion || appUserRef.current.userInDb.gBuild !== build)
    ) {
      appUserRef.current.userInDb.gAppVersion = appVersion;
      appUserRef.current.userInDb.gBuild = build;
      if (!wait) {
        setAppUser(appUserRef.current);
      }
      await pushUserFieldsAsync({gAppVersion: appVersion, gBuild: build});
    }

    syncFinished.current = true;
    setSyncFinished(true);
    setAppUser(appUserRef.current);
  };

  const ensureLocalImageFromRemote = async (targetUser, remoteUser) => {
    if (!targetUser || targetUser.imgSrc) {
      return false;
    }
    if (!remoteUser) {
      return false;
    }
    let remoteImgSrc = remoteUser.imgSrc;
    if (!remoteImgSrc) {
      remoteImgSrc = await remoteUser.getImage();
    }
    if (!remoteImgSrc) {
      return false;
    }

    try {
      await Filesystem.downloadFile({
        url: remoteImgSrc,
        directory: Directory.Data,
        path: `img${targetUser.uid}.jpg`
      });
      const fileUri = await Filesystem.getUri({
        path: `img${targetUser.uid}.jpg`,
        directory: Directory.Data
      }).catch(() => null);
      if (fileUri?.uri) {
        targetUser.imgSrc = Capacitor.convertFileSrc(fileUri.uri);
        return true;
      }
    } catch (error) {
      console.error("Error ensuring local user image:", error);
    }
    return false;
  };

  const saveImageToNativeFS = async (user) => {
    let imgSrc = user.imgSrc;
    if (!imgSrc) {
      imgSrc = await user.getImage();
    }
    if (imgSrc) {
      // Save the file to FileSystem in the app's directory
      const {path} = await Filesystem.downloadFile({
        url: imgSrc,
        directory: Directory.Data,
        path: `img${user.uid}.jpg`
      });
      return path;
    }
    return undefined;
  };

  const fetchDataRemote = async () => {
    /**
     * This function will fetch the user from the remote database (Firebase RealtimeDB).
     * For webapp/webview, first refresh subscription from Adapty to ensure latest data
     */

    // Refresh subscription from Adapty for webapp/webview
    if (!isAndroid && user?.uid) {
      console.log("Webapp: Refreshing subscription from Adapty before fetching user data");
      try {
        await SubscriptionService.refreshSubscriptionFromAdapty();
      } catch (error) {
        console.error("Error refreshing subscription from Adapty:", error);
        // Continue anyway - we'll use whatever is in Firebase
      }
    }

    const userInDb = await userCollection.getUserByIdAsync(user.uid, true, true);
    let userToReturn = null;
    if (userInDb !== null) {
      userToReturn = new AppUser(user, userInDb);
      localStorage.setItem("WS_User", user.uid);
      try {
        userToReturn.imgSrc = await userToReturn.getImage();
      } catch (error) {
        console.error("Error loading user image:", error);
        userToReturn.imgSrc = null;
      }
    }
    return userToReturn;
  };

  const registerNotifications = async () => {
    const permStatus = await FirebaseMessaging.checkPermissions();

    if (permStatus.receive !== "granted") {
      console.warn("Push notifications are not enabled");
      if (!isNil(appUserRef.current.userInDb.fcmToken)) {
        appUserRef.current.userInDb.fcmToken = null;
        setAppUser(appUserRef.current);
        await pushUserFieldsAsync({fcmToken: null});
      }
    } else {
      const {token} = await FirebaseMessaging.getToken();
      appUserRef.current.userInDb.fcmToken = token;
      setAppUser(appUserRef.current);
      await pushUserFieldsAsync({fcmToken: token});
    }
  };

  const updateAppUserLmd = () => {
    if (appUserRef.current.userInDb) {
      appUserRef.current.userInDb.lmd = new Date().getTime() / 1000;
      appUserRef.current.userInDb.lmdFull = new Date().toISOString();
    }
  };

  const pushUserFieldsAsync = async (fields) => {
    if (!appUserRef.current?.uid) {
      return;
    }
    updateAppUserLmd();
    await userCollection.updateUserAsync(
      appUserRef.current.uid,
      {
        ...fields,
        lmd: appUserRef.current.userInDb.lmd,
        lmdFull: appUserRef.current.userInDb.lmdFull
      },
      true
    );
  };

  const patchUserInDb = useCallback(
    (patch) => {
      if (!user?.uid || !appUserRef.current?.userInDb) {
        return null;
      }
      const mergedUserInDb = {
        ...appUserRef.current.userInDb,
        ...patch,
        ID: appUserRef.current.uid
      };
      const resolvedName = mergedUserInDb.displayName || mergedUserInDb.name || "";
      if (resolvedName) {
        mergedUserInDb.name = resolvedName;
        mergedUserInDb.displayName = resolvedName;
      }
      const nextAppUser = new AppUser(user, mergedUserInDb);
      appUserRef.current = nextAppUser;
      setAppUser(nextAppUser);
      return nextAppUser;
    },
    [user]
  );

  const values = useMemo(
    () => ({
      appUser,
      fetchData,
      patchUserInDb,
      user,
      claims,
      isAuthenticating,
      updateSubscriptionExpiryDateFromAdapty,
      setForceRefresh
    }),
    [appUser, user, claims, isAuthenticating, patchUserInDb]
  );

  return <AppUserContext.Provider value={values}>{children}</AppUserContext.Provider>;
}

function useAppUser() {
  return useContext(AppUserContext);
}

export {AppUserProvider, useAppUser};
